<script setup>

import {onMounted, onUnmounted, ref} from "vue";
import * as adminStore from "@/services/adminService";
import {formatDate} from "@/globals/utils/formatDate";
import FloatingButtonAction from "@/components/utils/FloatingButtonAction.vue";
import PaginationComponent from "@/components/utils/PaginationComponent.vue";
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import {trackScreenSize} from "@/globals/utils/getScreenSize";
import router from "@/router";
import {customToast} from "@/globals/utils/customToast";
import {deleteCar} from "@/services/carService";
import AssignDriverModal from "@/components/pages/admin/car/AssignDriverModal.vue";
import Swal from "sweetalert2";

const loading = ref(true);
const cars = ref([]);
const currentPage = ref(1);
const error = ref(null);
const screenInfo = ref({ width: 0, height: 0, deviceType: 'DESKTOP' });
let removeListener;
const showAssignDriverModal = ref(false);
const selectedCarId = ref('');

// Fetch cars based on the current page
const fetchCars = async () => {
  loading.value = true;
  try {
    cars.value = await adminStore.cars(currentPage.value);
  } catch (err) {
    error.value = 'Error loading drivers.';
    console.error(err);
  } finally {
    loading.value = false;
  }
};

// Edit car
const editCar = (car) => {
  router.push({ name: 'EditCar', params: { id: car._id } });
};

// Update page and fetch cars
const handlePageChange = async (page) => {
  currentPage.value = page;
  await router.push({ query: { page } });
  await fetchCars();
};

// Delete car
const deleteCarHandler = async (id) => {
  if (confirm('Are you sure you want to remove this car?')) {
    try {
      await deleteCar(id);
      cars.value.data = cars.value.data.filter(car => car._id !== id);
      customToast('Car deleted successfully', 'success');
    } catch (err) {
      customToast('Error deleting the car', 'error');
    }
  }
};

onMounted(() => {
  fetchCars();
  removeListener = trackScreenSize((info) => {
    screenInfo.value = info;
  });
});

onUnmounted(() => {
  if (removeListener) removeListener();
});

const showModalAssignDriver = (carId) => {
  selectedCarId.value = carId;
  showAssignDriverModal.value = true;
};

const unAssignDriver = async (carId) => {
  try {

    // Swal confirmation
    const {isConfirmed} = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action will unassign the driver from this car.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, unassign',
      cancelButtonText: 'Cancel',
    });

    if (!isConfirmed) return;

    await adminStore.unAssignDriver(carId);

    await fetchCars();
  } catch (err) {
    customToast('Error unassigning driver', 'error');
  }
};

</script>

<template>
  <AssignDriverModal
      :carId="selectedCarId"
      :showModal="showAssignDriverModal"
      @close="showAssignDriverModal = false"
      @updateCars="fetchCars" />
  <FloatingButtonAction path="/cars/create">
    <span class="material-icons mr-2">add_circle</span> Add Car
  </FloatingButtonAction>
  <LoadingComponent v-if="loading" />
  <div v-else class="overflow-x-auto bg-white dark:bg-gray-700 rounded-lg shadow">
    <table class="min-w-full table-auto">
      <thead class="bg-gray-100 dark:bg-gray-700 pb-3">
      <tr>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Car Type</th>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Brand</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Model</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">
          <span class="material-icons-round" style="font-size: 16px;" title="Passenger Capacity">people</span>
        </th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">
          <span class="material-icons-round" style="font-size: 16px;" title="Luggage Capacity">luggage</span>
        </th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Licence Plate</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Created At</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Updated At</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Driver</th>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Actions</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="car in cars.data" :key="car._id" class="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200 dark:odd:bg-gray-700 dark:even:bg-gray-600 dark:hover:bg-gray-500">
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
          <img :src="`/images/cars_icons/${car.carType}.png`" :alt="car.carType" class="w-16" :title="car.carType" />
        </td>
        <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ car.brand }}</td>
        <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ car.model }}</td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
          {{car.passengerCapacity}}
        </td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
          {{car.luggageCapacity}}
        </td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ car.licensePlate}}</td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ formatDate(car.createdAt) }}</td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ formatDate(car.updatedAt) }}</td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
          <div v-if="car.driver_id">
            {{ car.driver_id.name }}
            <button
                role="button"
                @click.prevent="unAssignDriver(car._id)"
                class="bg-gray-900 text-white rounded-full p-1 flex items-center justify-center w-5 h-5 float-start mr-2" title="Unassign Driver">
              <span class="material-icons-round" style="font-size: 16px;">close</span>
            </button>
          </div>
          <button
              v-else
              @click.prevent="showModalAssignDriver(car._id)"
              title="Assign Driver"
              class="flex items-center justify-center px-2 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-sm font-medium rounded"
          >
            <span class="material-icons-round" style="font-size: 16px;">person</span>
          </button>
        </td>
        <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
          <div class="flex items-center space-x-2">
            <button
                @click="editCar(car)"
                title="Edit Car"
                class="flex items-center justify-center px-2 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-sm font-medium rounded"
            >
              <span class="material-icons-round" style="font-size: 16px;">edit</span>
            </button>
            <button
                @click="deleteCarHandler(car._id)"
                title="Delete Car"
                class="flex items-center justify-center px-2 py-1 bg-red-300 hover:bg-red-400 dark:bg-red-700 dark:hover:bg-red-600 text-sm font-medium rounded"
            >
              <span class="material-icons-round" style="font-size: 16px;">delete</span>
            </button>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
    <PaginationComponent
        :currentPage="currentPage"
        :totalPages="cars.totalPages"
        :onChangePage="handlePageChange"
        class="mb-3"
    />
  </div>
</template>